// ============================================================================
// RENDER RECEIPT PDF — Print-to-PDF via browser print window
//
// Approach: open a new window, inject fully self-contained HTML, and call
// window.print(). The user's browser "Save as PDF" produces the file.
// No external dependencies required.
//
// The receipt model comes from buildFullReceiptModel.js.
// This file contains no PHI logic — it only renders whatever is in the model.
//
// Sections rendered (spec order):
//   Header  — outcome badge, run ID, generated time
//   1) Patient (PHI): Identity + Contact + Insurance
//   2) Referring Provider
//   3) Referral Summary
//   4) Criteria & Completeness Snapshot
//   5) Attachments
//   Footer — PHI notice + "Access logged"
// ============================================================================

// ── Helpers ───────────────────────────────────────────────────────────────────

const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const field = (label, value, fallback = '—') =>
  `<tr>
    <td class="label">${esc(label)}</td>
    <td class="value">${esc(value || fallback)}</td>
  </tr>`;

const sectionHeader = (title, subtitle = '') =>
  `<div class="section-header">
    <span class="section-title">${esc(title)}</span>
    ${subtitle ? `<span class="section-sub">${esc(subtitle)}</span>` : ''}
  </div>`;

const OUTCOME_COLORS = {
  routine: '#059669',  // emerald-600
  urgent:  '#dc2626',  // red-600
  not_yet: '#d97706',  // amber-600
};

const fmtBytes = (n) => {
  if (n == null || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Main renderer ─────────────────────────────────────────────────────────────

/**
 * Open a print window with the formatted PHI receipt and trigger print dialog.
 *
 * @param {object} model  — output of buildFullReceiptModel()
 * @returns {Promise<void>}
 */
export const renderReceiptPdf = (model) => {
  return new Promise((resolve, reject) => {
    try {
      const {
        meta, outcome, pathway, provider, submittedAt,
        patient, contact, insurance, criteria, attachments,
      } = model;

      const outcomeColor = OUTCOME_COLORS[outcome.status] || '#6b7280';
      const generatedStr = new Date(meta.generatedAt).toLocaleString('en-US', {
        dateStyle: 'long', timeStyle: 'short',
      });
      const submittedStr = submittedAt
        ? new Date(submittedAt).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })
        : '—';

      // ── CSS ──
      const css = `
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 10pt; color: #111; background: #fff; padding: 0; }
        .page { max-width: 720px; margin: 0 auto; padding: 24px 32px 32px; }
        /* Header */
        .receipt-header { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 2px solid #111; padding-bottom: 12px; margin-bottom: 16px; }
        .brand { font-size: 11pt; font-weight: 700; letter-spacing: .04em; color: #111; text-transform: uppercase; }
        .brand-sub { font-size: 8pt; color: #666; margin-top: 2px; }
        .outcome-badge { text-align: right; }
        .outcome-label { font-size: 16pt; font-weight: 900; letter-spacing: .06em; }
        .outcome-sub { font-size: 8pt; color: #555; margin-top: 2px; }
        .meta-bar { display: flex; gap: 24px; flex-wrap: wrap; background: #f5f5f5; border: 1px solid #ddd; border-radius: 4px; padding: 8px 12px; margin-bottom: 16px; font-size: 8.5pt; }
        .meta-item { display: flex; flex-direction: column; gap: 1px; }
        .meta-item .ml { font-size: 7pt; color: #888; text-transform: uppercase; letter-spacing: .04em; }
        .meta-item .mv { font-weight: 600; }
        /* Sections */
        .section { margin-bottom: 16px; page-break-inside: avoid; }
        .section-header { display: flex; align-items: baseline; gap: 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 8px; }
        .section-title { font-size: 9.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; }
        .section-sub { font-size: 8pt; color: #888; }
        /* Tables */
        table.fields { width: 100%; border-collapse: collapse; }
        table.fields td { padding: 3px 6px 3px 0; vertical-align: top; font-size: 9.5pt; }
        td.label { width: 38%; color: #555; padding-right: 12px; }
        td.value { font-weight: 500; }
        /* Criteria */
        .criteria-context { font-size: 8.5pt; color: #555; margin-bottom: 8px; background: #f9f9f9; border-left: 3px solid #ddd; padding: 4px 8px; }
        .criteria-row { display: flex; align-items: flex-start; gap: 8px; padding: 3px 0; font-size: 9.5pt; }
        .criteria-check { font-size: 10pt; flex-shrink: 0; margin-top: 1px; }
        .criteria-check.ok  { color: #059669; }
        .criteria-check.bad { color: #dc2626; }
        .criteria-label  { display: flex; flex-direction: column; gap: 1px; }
        .criteria-answer { font-size: 8.5pt; color: #1a1a1a; font-weight: 600; margin-top: 1px; }
        .criteria-help   { font-size: 7.5pt; color: #888; font-style: italic; }
        /* Red flags */
        .redflag-row { display: flex; align-items: center; gap: 6px; padding: 2px 0; font-size: 9.5pt; color: #dc2626; }
        /* Override */
        .override-box { background: #f5f0ff; border: 1px solid #c4b5fd; border-radius: 4px; padding: 8px 12px; margin-bottom: 12px; font-size: 9pt; }
        .override-box .ob-title { font-weight: 700; color: #7c3aed; margin-bottom: 3px; }
        /* PHI notice */
        .phi-notice { background: #fef3c7; border: 1px solid #fcd34d; border-radius: 4px; padding: 6px 10px; font-size: 8.5pt; color: #92400e; margin-bottom: 12px; }
        /* Attachments table */
        table.att { width: 100%; border-collapse: collapse; }
        table.att th { text-align: left; font-size: 8.5pt; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #555; border-bottom: 1px solid #ccc; padding: 3px 6px 3px 0; }
        table.att td { font-size: 9.5pt; padding: 3px 6px 3px 0; border-bottom: 1px solid #eee; }
        .status-ready { color: #059669; font-weight: 600; }
        .status-fail  { color: #dc2626; font-weight: 600; }
        /* Footer */
        .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 8pt; color: #888; display: flex; justify-content: space-between; }
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .page { padding: 0; max-width: 100%; }
          .no-print { display: none; }
        }
      `;

      // ── Section 1: Patient ──
      let patientHtml = '';
      if (patient || contact || insurance) {
        let rows = '';
        if (patient) {
          rows += field('Name', patient.fullName);
          rows += field('Date of Birth', patient.dob);
          rows += field('Sex', patient.sex);
          for (const id of (patient.identifiers || [])) {
            rows += field(id.type || 'Identifier', id.value);
          }
        }
        if (contact) {
          if (contact.phone) rows += field('Phone', contact.phone);
          if (contact.email) rows += field('Email', contact.email);
          if (contact.address) rows += field('Address', contact.address);
        }
        if (insurance) {
          if (insurance.payerName) rows += field('Payer', insurance.payerName);
          if (insurance.memberId)  rows += field('Member ID', insurance.memberId);
          if (insurance.groupNumber) rows += field('Group #', insurance.groupNumber);
          if (insurance.planType)  rows += field('Plan Type', insurance.planType);
        }
        patientHtml = `
          <div class="section">
            ${sectionHeader('Patient', 'PHI')}
            <div class="phi-notice">⚠ Contains Protected Health Information (PHI) — Handle per HIPAA / tenant policy. Access logged.</div>
            <table class="fields"><tbody>${rows}</tbody></table>
          </div>`;
      } else {
        patientHtml = `
          <div class="section">
            ${sectionHeader('Patient', 'PHI')}
            <p style="font-size:9pt;color:#888;">Patient data unavailable — PHI was not loaded for this receipt.</p>
          </div>`;
      }

      // ── Section 2: Provider ──
      const providerHtml = `
        <div class="section">
          ${sectionHeader('Referring Provider')}
          <table class="fields"><tbody>
            ${field('Name', provider.name)}
            ${field('Submitted', submittedStr)}
          </tbody></table>
        </div>`;

      // ── Section 3: Referral Summary ──
      const summaryHtml = `
        <div class="section">
          ${sectionHeader('Referral Summary')}
          <table class="fields"><tbody>
            ${field('Specialty', pathway.specialtyName)}
            ${field('Pack', `${pathway.packName} v${pathway.packVersion}`)}
            ${field('Visit Type', pathway.visitType)}
            ${field('Reason for Escalation', pathway.reason)}
            ${field('Outcome', `${outcome.label} (${outcome.completenessScore}% complete)`)}
          </tbody></table>
        </div>`;

      // ── Section 4: Criteria ──
      const criteriaRowsHtml = criteria.rows.map(r =>
        `<div class="criteria-row">
          <span class="criteria-check ${r.provided ? 'ok' : 'bad'}">${r.provided ? '✓' : '✗'}</span>
          <span class="criteria-label">
            <span>${esc(r.label)}</span>
            ${r.provided && r.answerText ? `<span class="criteria-answer">${esc(r.answerText)}</span>` : ''}
            ${r.helpText ? `<span class="criteria-help">${esc(r.helpText)}</span>` : ''}
          </span>
        </div>`
      ).join('');

      const redFlagsHtml = criteria.triggeredRedFlags.map(label =>
        `<div class="redflag-row"><span>⚠</span><span>${esc(label)}</span></div>`
      ).join('');

      const missingHtml = (!outcome.wasOverridden && criteria.missingFields.length > 0)
        ? `<div style="margin-top:8px"><p style="font-size:9pt;font-weight:600;color:#d97706;margin-bottom:4px">Missing Required Items:</p>
           ${criteria.missingFields.map(l => `<div style="font-size:9.5pt;color:#d97706;padding:2px 0">• ${esc(l)}</div>`).join('')}
           </div>`
        : '';

      const overrideHtml = outcome.wasOverridden
        ? `<div class="override-box">
            <div class="ob-title">Gate Override Applied</div>
            <div>Reason: ${esc(outcome.overrideReason)}</div>
            ${outcome.overrideNote ? `<div style="color:#555;font-style:italic;margin-top:2px">"${esc(outcome.overrideNote)}"</div>` : ''}
            <div style="font-size:8pt;color:#888;margin-top:4px">This override is logged and auditable. Submitted as ROUTINE.</div>
           </div>`
        : '';

      const criteriaHtml = `
        <div class="section">
          ${sectionHeader('Criteria & Completeness Snapshot')}
          <div class="criteria-context">
            Clinical criteria for <strong>${esc(pathway.specialtyName)}</strong> &nbsp;·&nbsp;
            Pack: <strong>${esc(pathway.packName)} v${esc(pathway.packVersion)}</strong>
          </div>
          ${overrideHtml}
          <p style="font-size:9pt;font-weight:600;margin-bottom:6px">Required Items:</p>
          ${criteriaRowsHtml || '<p style="font-size:9pt;color:#888">No required criteria for this pack.</p>'}
          ${redFlagsHtml ? `<p style="font-size:9pt;font-weight:600;margin-top:10px;margin-bottom:4px;color:#dc2626">Red Flags Triggered:</p>${redFlagsHtml}` : ''}
          ${missingHtml}
        </div>`;

      // ── Section 5: Attachments ──
      let attachHtml = '';
      if (attachments.length > 0) {
        const rows = attachments.map((att, i) => {
          const uploadedStr = att.uploadedAt
            ? new Date(att.uploadedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
            : '—';
          const statusCls = att.status === 'READY' ? 'status-ready' : att.status === 'FAILED' ? 'status-fail' : '';
          return `<tr>
            <td>${i + 1}</td>
            <td>${esc(att.typeLabel)}</td>
            <td class="${statusCls}">${esc(att.status)}</td>
            <td>${esc(fmtBytes(att.sizeBytes))}</td>
            <td>${esc(uploadedStr)}</td>
          </tr>`;
        }).join('');
        attachHtml = `
          <div class="section">
            ${sectionHeader('Attachments', 'PHI — manifest only; no file contents')}
            <table class="att">
              <thead><tr><th>#</th><th>Type</th><th>Status</th><th>Size</th><th>Uploaded</th></tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </div>`;
      } else {
        attachHtml = `
          <div class="section">
            ${sectionHeader('Attachments')}
            <p style="font-size:9pt;color:#888;">No attachments on this referral.</p>
          </div>`;
      }

      // ── Full HTML ──
      const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Referral Receipt — ${esc(meta.runId)}</title>
  <style>${css}</style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="receipt-header">
    <div>
      <div class="brand">ReferralGate</div>
      <div class="brand-sub">Clinical Referral Receipt — CONFIDENTIAL</div>
    </div>
    <div class="outcome-badge">
      <div class="outcome-label" style="color:${outcomeColor}">${esc(outcome.label)}</div>
      <div class="outcome-sub">${esc(outcome.completenessScore)}% complete · ${outcome.wasOverridden ? 'Override applied' : 'Gate decision'}</div>
    </div>
  </div>

  <!-- Meta bar -->
  <div class="meta-bar">
    <div class="meta-item"><span class="ml">Referral ID</span><span class="mv">${esc(meta.referralId)}</span></div>
    <div class="meta-item"><span class="ml">Specialty</span><span class="mv">${esc(pathway.specialtyName)}</span></div>
    <div class="meta-item"><span class="ml">Pack</span><span class="mv">${esc(pathway.packName)} v${esc(pathway.packVersion)}</span></div>
    <div class="meta-item"><span class="ml">Generated</span><span class="mv">${esc(generatedStr)}</span></div>
  </div>

  ${patientHtml}
  ${providerHtml}
  ${summaryHtml}
  ${criteriaHtml}
  ${attachHtml}

  <!-- Footer -->
  <div class="footer">
    <div>ReferralGate — ${esc(meta.tenantId)} · PHI access logged · Referral ${esc(meta.referralId)}</div>
    <div>Generated ${esc(generatedStr)}</div>
  </div>

</div>
</body>
</html>`;

      // ── Open print window ──
      const win = window.open('', '_blank', 'width=850,height=1100');
      if (!win) {
        reject(new Error('Popup blocked — allow popups for this site to download PDF'));
        return;
      }
      win.document.write(html);
      win.document.close();
      // Let the browser render, then print
      win.onload = () => {
        try {
          win.focus();
          win.print();
          resolve();
        } catch (e) {
          reject(e);
        }
      };
      // Fallback if onload already fired
      setTimeout(() => {
        try {
          if (!win.closed) {
            win.focus();
            win.print();
            resolve();
          }
        } catch {
          // already resolved or window closed
        }
      }, 800);
    } catch (err) {
      reject(err);
    }
  });
};
